import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { categories, siteInfo } from "@/mocks/siteData";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(mobileSearch.toLowerCase())
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-background-50/40 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="text-lg md:text-xl font-heading font-bold text-foreground-950 tracking-tight whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer"
          >
            superbuyspreadsheet<span className="text-accent-500">.bar</span>
          </Link>

          <div className="hidden md:flex items-center gap-2.5">
            <Link
              to="/"
              className="px-4 py-2.5 rounded-lg text-foreground-700 text-sm font-medium hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>

            <Link
              to="/article/superbuy-spreadsheet-guide"
              className="px-4 py-2.5 rounded-lg text-foreground-700 text-sm font-medium hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-foreground-200 bg-background-50 text-foreground-700 text-sm font-medium hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                <span>Categories</span>
                <i className={`${isDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} w-3.5 h-3.5 flex items-center justify-center transition-transform`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-foreground-200 rounded-xl shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-accent-500`}></i>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              superbuy spreadsheet
            </a>
            <a
              href={siteInfo.mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 bg-background-100 border border-foreground-200 cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-lg`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-5 border-t border-foreground-100/50 animate-[fadeIn_0.2s_ease-out]">
            <div className="pt-4 space-y-2">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 text-sm font-semibold hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                <span>Home</span>
              </Link>

              <Link
                to="/article/superbuy-spreadsheet-guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 text-sm font-semibold hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                <span>Shopping Guide</span>
              </Link>
            </div>

            <div className="pt-4">
              <div className="flex items-center gap-2 px-3 mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">Categories</span>
                <div className="flex-1 h-px bg-foreground-200/50"></div>
              </div>

              <div className="px-3 mb-3">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={mobileSearch}
                    onChange={(e) => setMobileSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-100 border border-foreground-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-1.5 px-3 max-h-72 overflow-y-auto">
                {filteredCategories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0`}></i>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
                {filteredCategories.length === 0 && (
                  <div className="col-span-2 text-center py-4 text-sm text-foreground-400">No categories found</div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4 px-3">
              <a
                href={siteInfo.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                superbuy spreadsheet
              </a>
              <a
                href={siteInfo.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold text-center hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                buy it now
              </a>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}